import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useAnimate } from "framer-motion";

export default function IntroGate({
  mainLogo = "/img/Logo/LOGO-05.webp",
  middleLogo = "/img/Logo/LOGO-05.webp",
  b2bImage = "/img/introb2b.webp",
  b2cImage = "/img/introb2c.webp",
  b2cTo = "/b2c",
  b2bTo = "/b2b",
  subtitle = "潔 淨 初 心    守 護 健 康",
  showEveryLoad = false,
  enableSplitChoice = true,
}) {
  const nav = useNavigate();
  const [show, setShow] = useState(
    showEveryLoad ? true : !sessionStorage.getItem("introSeen"),
  );
  const [phase, setPhase] = useState("logo");
  const [hovered, setHovered] = useState(null);
  const [scope, animate] = useAnimate();
  const clickingRef = useRef(false);
  const isMobile = useMediaQuery("(max-width:900px)");

  const proimg = "/img/subtitle/PRO.webp";
  const prowhiteimg = "/img/subtitle/pro-white.webp";
  const lifeimg = "/img/subtitle/LIFE.webp";
  const lifewhiteimg = "/img/subtitle/life-white.webp";

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const safeAnimate = async (selector, keyframes, options) => {
    const root = scope.current;
    if (!root) return;
    const el =
      typeof selector === "string" ? root.querySelector(selector) : selector;
    if (!el) return;
    return animate(el, keyframes, options);
  };

  useEffect(() => {
    if (!show || phase !== "logo") return;
    let alive = true;

    const run = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      if (!alive) return;

      await safeAnimate(
        "#brand-logo",
        { opacity: 1, scale: 1 },
        { type: "spring", stiffness: 260, damping: 24, duration: 0.7 },
      );
      await safeAnimate("#brand-logo", { scale: 0.97 }, { duration: 0.18 });
      await safeAnimate("#brand-logo", { scale: 1 }, { duration: 0.18 });

      if (!alive) return;

      await safeAnimate(
        "#brand-subtitle",
        {
          opacity: 1,
          clipPath: ["inset(0 100% 0 0)", "inset(0 0 0 0)"],
        },
        { duration: 0.6, ease: "easeOut" },
      );

      if (!alive) return;

      await new Promise((r) => setTimeout(r, 400));
      if (!alive) return;

      await Promise.all([
        safeAnimate(
          "#brand-logo",
          { opacity: 0, y: -24, scale: 0.9 },
          { duration: 0.5, ease: "easeInOut" },
        ),
        safeAnimate(
          "#brand-subtitle",
          { opacity: 0, y: 12 },
          { duration: 1, ease: "easeInOut" },
        ),
      ]);

      if (!alive) return;

      if (!enableSplitChoice) {
        sessionStorage.setItem("introSeen", "1");
        setShow(false);
        nav(b2bTo);
        return;
      }

      setPhase("split");
    };

    run();
    return () => {
      alive = false;
    };
  }, [show, phase, animate, scope, enableSplitChoice, b2bTo, nav]);

  const [split, setSplit] = useState(55);
  const logoSplitAngle = 4;
  useEffect(() => {
    const apply = () => {
      const w = window.innerWidth;
      setSplit(w < 1200 ? 58 : 55);
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);
  const logoDiagonalClip = useMemo(() => {
    const offset = logoSplitAngle;

    return {
      left: `polygon(-20% -20%, calc(50% + ${offset}%) -20%, calc(50% - ${offset}%) 120%, -20% 120%)`,
      right: `polygon(calc(50% + ${offset}%) -20%, 120% -20%, 120% 120%, calc(50% - ${offset}%) 120%)`,
    };
  }, [logoSplitAngle]);
  if (!show) return null;

  if (!isMobile) {
    const leftBright =
      hovered === "left" ? 1.08 : hovered === "right" ? 0.72 : 1;
    const rightBright =
      hovered === "right" ? 1.08 : hovered === "left" ? 0.72 : 1;

    const leftGlowOpacity =
      hovered === "left"
        ? [0.9, 0.98]
        : hovered === "right"
          ? [0.16, 0.22]
          : [0.66, 0.74];

    const rightGlowOpacity =
      hovered === "right"
        ? [0.9, 0.98]
        : hovered === "left"
          ? [0.16, 0.22]
          : [0.66, 0.74];

    const glowScaleLeft =
      hovered === "left"
        ? [1.0, 1.08]
        : hovered === "right"
          ? [0.99, 1.02]
          : [0.995, 1.04];

    const glowScaleRight =
      hovered === "right"
        ? [1.0, 1.08]
        : hovered === "left"
          ? [0.99, 1.02]
          : [0.995, 1.04];

    const handlePick = async (side) => {
      if (clickingRef.current) return;
      clickingRef.current = true;
      const to = side === "left" ? b2bTo : b2cTo;
      try {
        if (side === "left") {
          await safeAnimate("#right", { opacity: 0 }, { duration: 0.2 });
          await safeAnimate(
            "#left",
            { clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)" },
            { duration: 0.5, ease: "easeInOut" },
          );
        } else {
          await safeAnimate("#left", { opacity: 0 }, { duration: 0.2 });
          await safeAnimate(
            "#right",
            { clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)" },
            { duration: 0.5, ease: "easeInOut" },
          );
        }
      } finally {
        sessionStorage.setItem("introSeen", "1");
        setShow(false);
        nav(to);
      }
    };

    return (
      <AnimatePresence>
        <Box
          ref={scope}
          component={motion.div}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          {phase === "logo" && (
            <Box
              id="brand"
              component={motion.div}
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <motion.img
                  id="brand-logo"
                  src={mainLogo}
                  alt="brand"
                  initial={{ opacity: 0, scale: 0.8 }}
                  style={{
                    width: "min(50vw, 320px)",
                    height: "auto",
                    position: "relative",
                    zIndex: 2,
                  }}
                />
                <motion.div
                  id="brand-subtitle"
                  initial={{
                    opacity: 0,
                    clipPath: "inset(0 100% 0 0)",
                  }}
                  style={{
                    position: "relative",
                    zIndex: 2,
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 25,
                      fontWeight: 700,
                      whiteSpace: "pre",
                      fontFamily: "LINE Seed TW",
                    }}
                  >
                    {subtitle}
                  </Typography>
                </motion.div>
              </Box>
            </Box>
          )}

          {phase === "split" && (
            <>
              {/* LEFT */}
              <motion.div
                id="left"
                initial={{ y: "-100%" }}
                animate={{ y: 0, filter: `brightness(${leftBright})` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                onMouseEnter={() => setHovered("left")}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handlePick("left")}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100vw",
                  height: "100vh",
                  cursor: "pointer",
                  clipPath: `polygon(0 0, ${split}% 0, ${
                    100 - split
                  }% 100%, 0% 100%)`,
                  zIndex: 2,
                  overflow: "hidden",
                  backgroundImage: `url(${b2bImage})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "left center",
                  backgroundColor: "#f5f5f5",
                  willChange: "clip-path, filter, transform",
                }}
              />

              {/* RIGHT */}
              <motion.div
                id="right"
                initial={{ y: "100%" }}
                animate={{ y: 0, filter: `brightness(${rightBright})` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                onMouseEnter={() => setHovered("right")}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handlePick("right")}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100vw",
                  height: "100vh",
                  cursor: "pointer",
                  clipPath: `polygon(${split}% 0, 100% 0, 100% 100%, ${
                    100 - split
                  }% 100%)`,
                  zIndex: 1,
                  overflow: "hidden",
                  backgroundImage: `url(${b2cImage})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "right center",
                  backgroundColor: "#f5f5f5",
                  willChange: "clip-path, filter, transform",
                }}
              />

              {/* Diagonal blur separator */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  zIndex: 4,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-25vh",
                    bottom: "-25vh",
                    left: "50%",
                    width: "18vw",
                    transform: "translateX(-50%) skewX(-18deg)",
                    background:
                      "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.95), rgba(255,255,255,0))",
                    filter: "blur(30px)",
                    mixBlendMode: "screen",
                    opacity: 1,
                  }}
                />
              </Box>

              {/* PRO badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: "5%",
                  left: "10%",
                  transform: "translateX(-50%)",
                  width: "13vw",
                  pointerEvents: "none",
                  zIndex: 6,
                }}
              >
                <motion.img
                  src={prowhiteimg}
                  alt="PRO (white)"
                  initial={false}
                  animate={{
                    opacity: hovered === "left" ? 0 : 1,
                    scale: hovered === "left" ? 0.97 : 0.94,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  style={{
                    width: "100%",
                    height: "auto",
                    position: "absolute",
                    inset: 0,
                    transformOrigin: "center center",
                  }}
                />

                <motion.img
                  src={proimg}
                  alt="PRO (color)"
                  initial={false}
                  animate={{
                    opacity: hovered === "left" ? 1 : 0,
                    scale: hovered === "left" ? 1.05 : 1,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  style={{
                    width: "100%",
                    height: "auto",
                    transformOrigin: "center center",
                  }}
                />
              </Box>

              {/* PRO text */}
              <Box
                sx={{
                  position: "absolute",
                  top: "20%",
                  left: "8%",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                  zIndex: 6,
                  textAlign: "start",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{
                    opacity: hovered === "left" ? 1 : 0,
                    y: hovered === "left" ? 0 : 5,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontFamily: "LINE Seed TW",
                      fontSize: { xs: "3.2vw", md: 30 },
                      color: "#615f5fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    食品衛生專家
                  </Typography>
                </motion.div>
              </Box>

              {/* LIFE badge - moved to TOP RIGHT */}
              <Box
                sx={{
                  position: "absolute",
                  top: "6%",
                  right: "5%",
                  width: "12vw",
                  pointerEvents: "none",
                  zIndex: 6,
                }}
              >
                <motion.img
                  src={lifewhiteimg}
                  alt="LIFE (white)"
                  initial={false}
                  animate={{
                    opacity: hovered === "right" ? 0 : 1,
                    scale: hovered === "right" ? 0.97 : 0.94,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  style={{
                    width: "100%",
                    height: "auto",
                    position: "absolute",
                    inset: 0,
                    transformOrigin: "center center",
                  }}
                />

                <motion.img
                  src={lifeimg}
                  alt="LIFE (color)"
                  initial={false}
                  animate={{
                    opacity: hovered === "right" ? 1 : 0,
                    scale: hovered === "right" ? 1.05 : 1,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  style={{
                    width: "100%",
                    height: "auto",
                    transformOrigin: "center center",
                  }}
                />
              </Box>

              {/* LIFE text - moved to TOP RIGHT */}
              <Box
                sx={{
                  position: "absolute",
                  top: "18%",
                  right: "6%",
                  pointerEvents: "none",
                  zIndex: 6,
                  textAlign: "center",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{
                    opacity: hovered === "right" ? 1 : 0,
                    y: hovered === "right" ? 0 : 5,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontFamily: "LINE Seed TW",
                      fontSize: { xs: "3.2vw", md: 30 },
                      color: "rgb(232, 137, 175)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    購物網
                  </Typography>
                </motion.div>
              </Box>

              {/* Hidden SVG filter defs for merged whole-word white backing */}
              <Box
                component="svg"
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <defs>
                  <filter
                    id="seho-word-backing"
                    x="-50%"
                    y="-60%"
                    width="200%"
                    height="220%"
                    colorInterpolationFilters="sRGB"
                  >
                    {/* <!-- BIG expansion --> */}
                    <feMorphology
                      in="SourceAlpha"
                      operator="dilate"
                      radius="18"
                      result="expanded"
                    />

                    {/* <!-- heavy blur to kill bumps --> */}
                    <feGaussianBlur
                      in="expanded"
                      stdDeviation="10"
                      result="blurred"
                    />

                    {/* <!-- strong threshold (flatten bumps) --> */}
                    <feColorMatrix
                      in="blurred"
                      type="matrix"
                      values="
                    1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    0 0 0 40 -18
                  "
                      result="flattened"
                    />

                    {/* <!-- final soft edge --> */}
                    <feGaussianBlur
                      in="flattened"
                      stdDeviation="4"
                      result="soft"
                    />

                    {/* <!-- paint white --> */}
                    <feFlood
                      floodColor="#ffffff"
                      floodOpacity="1"
                      result="whiteFill"
                    />
                    <feComposite in="whiteFill" in2="soft" operator="in" />
                  </filter>
                </defs>
              </Box>
              {/* Center SEHO with merged whole-word white backing */}
              <Box
                sx={{
                  pointerEvents: "none",
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    width: "min(50vw, 560px)",
                    overflow: "visible",
                  }}
                >
                  {/* LEFT half backing */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      overflow: "visible",
                      clipPath: logoDiagonalClip.left,
                      zIndex: 0,
                    }}
                  >
                    <motion.img
                      src={middleLogo}
                      alt=""
                      initial={false}
                      animate={{
                        opacity: leftGlowOpacity,
                        scale: glowScaleLeft,
                      }}
                      transition={{
                        duration: hovered ? 1.9 : 2.3,
                        repeat: Infinity,
                        repeatType: "mirror",
                        ease: "easeInOut",
                      }}
                      style={{
                        position: "absolute",
                        left: "-3%",
                        top: "-5%",
                        width: "105%",
                        height: "105%",
                        objectFit: "contain",
                        transformOrigin: "center center",
                        filter: "url(#seho-word-backing)",
                        overflow: "visible",
                      }}
                    />
                  </Box>

                  {/* RIGHT half backing */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      overflow: "visible",
                      clipPath: logoDiagonalClip.right,
                      zIndex: 0,
                    }}
                  >
                    <motion.img
                      src={middleLogo}
                      alt=""
                      initial={false}
                      animate={{
                        opacity: rightGlowOpacity,
                        scale: glowScaleRight,
                      }}
                      transition={{
                        duration: hovered ? 1.9 : 2.3,
                        repeat: Infinity,
                        repeatType: "mirror",
                        ease: "easeInOut",
                      }}
                      style={{
                        position: "absolute",
                        left: "-3%",
                        top: "-5%",
                        width: "105%",
                        height: "105%",
                        objectFit: "contain",
                        transformOrigin: "center center",
                        filter: "url(#seho-word-backing)",
                        overflow: "visible",
                      }}
                    />
                  </Box>

                  {/* actual logo */}
                  <motion.img
                    src={middleLogo}
                    alt="SEHO"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 22,
                    }}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "auto",
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </AnimatePresence>
    );
  }

  const handlePickMobile = async (which) => {
    if (clickingRef.current) return;
    clickingRef.current = true;
    const to = which === "pro" ? b2bTo : b2cTo;
    try {
      await safeAnimate(
        `#tile-${which}`,
        { scale: [1, 0.98, 1.02, 1] },
        { duration: 0.28, ease: "easeInOut" },
      );
    } finally {
      sessionStorage.setItem("introSeen", "1");
      setShow(false);
      nav(to);
    }
  };

  return (
    <AnimatePresence>
      <Box
        ref={scope}
        component={motion.div}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 20000,
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        {phase === "logo" && (
          <Box
            id="brand"
            component={motion.div}
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <motion.img
                id="brand-logo"
                src={mainLogo}
                alt="brand"
                initial={{ opacity: 0, scale: 0.8 }}
                style={{
                  width: 200,
                  height: "auto",
                  position: "relative",
                  zIndex: 2,
                }}
              />
              <motion.div
                id="brand-subtitle"
                initial={{
                  opacity: 0,
                  clipPath: "inset(0 100% 0 0)",
                }}
                style={{ overflow: "hidden", zIndex: 2 }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {subtitle}
                </Typography>
              </motion.div>
            </Box>
          </Box>
        )}

        {phase === "split" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              p: 2.5,
              pt: 5,
              gap: 3,
            }}
          >
            <motion.img
              src={middleLogo}
              alt="SEHO"
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              style={{ width: 230, height: "auto" }}
            />

            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                border: (t) => `1px solid ${t.palette.text.disabled}`,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 1,
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 1.2,
                }}
              >
                <Box
                  component="img"
                  src={proimg}
                  alt="PRO"
                  sx={{ width: 80, height: "auto" }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontFamily: "LINE Seed TW",
                    fontSize: { xs: 15, md: 30 },
                    color: "#615f5fff",
                    whiteSpace: "nowrap",
                    px: 1.5,
                    alignContent: "end",
                  }}
                >
                  食品衛生專家
                </Typography>
              </Box>
              <motion.button
                id="tile-pro"
                onClick={() => handlePickMobile("pro")}
                whileTap={{ scale: 0.97 }}
                style={{
                  all: "unset",
                  display: "block",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 10",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={b2bImage}
                    alt="PRO / B2B"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </motion.button>
            </Box>

            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                border: (t) => `1px solid ${t.palette.text.disabled}`,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 1,
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 1.2,
                }}
              >
                <Box
                  component="img"
                  src={lifeimg}
                  alt="LIFE"
                  sx={{ width: 80, height: "auto" }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontFamily: "LINE Seed TW",
                    fontSize: { xs: "3.2vw", md: 30 },
                    color: "rgb(232, 137, 175)",
                    whiteSpace: "nowrap",
                    px: 1.5,
                    alignContent: "end",
                  }}
                >
                  購物網
                </Typography>
              </Box>
              <motion.button
                id="tile-life"
                onClick={() => handlePickMobile("life")}
                whileTap={{ scale: 0.97 }}
                style={{
                  all: "unset",
                  display: "block",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 10",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={b2cImage}
                    alt="LIFE / B2C"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </motion.button>
            </Box>
          </Box>
        )}
      </Box>
    </AnimatePresence>
  );
}
